import React, { useState, useMemo } from 'react';
import { Modal, Field, Input, Btn, SectionHeader, AlertBanner } from '../components/UI';
import { fmtM, fmtDate, todayStr, SOCIOS, COLORS_SOCIOS, ini, costosProrrateadosSemana } from '../utils';
import { showToast } from '../components/UI';

// Semana lunes-domingo
function getSemanaBounds(offsetWeeks = 0) {
  const d = new Date(); d.setHours(0,0,0,0);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const lunes = new Date(d.getTime() + (diff + offsetWeeks * 7) * 86400000);
  const domingo = new Date(lunes.getTime() + 6 * 86400000);
  const fmt = dd => dd.getFullYear() + '-' + String(dd.getMonth()+1).padStart(2,'0') + '-' + String(dd.getDate()).padStart(2,'0');
  return { desde: fmt(lunes), hasta: fmt(domingo), key: fmt(lunes) };
}

function calcGananciaSemana(movimientos, costosMes, desde, hasta, margen, resFinPct) {
  const ventas = Object.values(movimientos || {})
    .filter(m => m.tipo === 'venta' && m.fecha >= desde && m.fecha <= hasta)
    .reduce((a, m) => a + (parseFloat(m.monto) || 0), 0);
  const gastosVar = Object.values(movimientos || {})
    .filter(m => m.tipo === 'gasto' && m.fecha >= desde && m.fecha <= hasta)
    .reduce((a, m) => a + (parseFloat(m.monto) || 0), 0);
  const utilBruta = ventas * margen;
  const costoProrr = costosProrrateadosSemana(costosMes, desde, hasta, movimientos);
  const resFin = ventas * resFinPct;
  const utilNeta = utilBruta - costoProrr - gastosVar - resFin;
  return { ventas, utilBruta, costoProrr, gastosVar, resFin, utilNeta };
}

export default function Ganancias({ store, setItem }) {
  const { movimientos, costosMes, socios, semanas } = store;

  const [margen, setMargen] = useState(() => parseFloat(localStorage.getItem('margen-bruto') || '0.34'));
  const [resFinPct, setResFinPct] = useState(() => parseFloat(localStorage.getItem('res-fin-pct') || '0.03'));
  const [distModal, setDistModal] = useState(null);
  const [distForm, setDistForm] = useState({});
  const [resetConfirm, setResetConfirm] = useState(null); // semanaKey

  const hoy = todayStr();

  // Build last 6 weeks
  const semanasList = useMemo(() => {
    const list = [];
    for (let i = 0; i >= -5; i--) {
      const bounds = getSemanaBounds(i);
      const cierre = semanas?.[bounds.key];
      // Semanas cerradas: usar utilNeta guardada (no recalcular desde movimientos)
      // Semana actual (i===0): calcular en tiempo real
      const datos = (cierre?.cerrada)
        ? { ventas: 0, utilBruta: 0, costoProrr: 0, gastosVar: 0, resFin: 0, utilNeta: cierre.utilNeta || 0 }
        : calcGananciaSemana(movimientos, costosMes, bounds.desde, bounds.hasta, margen, resFinPct);
      list.push({ ...bounds, cierre, datos, ajuste: 0, isCurrent: i === 0 });
    }
    return list;
  }, [movimientos, costosMes, semanas, margen, resFinPct]);

  // Ganancia acumulada: usa utilNeta guardada - lo distribuido
  const gananciasAcumuladas = useMemo(() => {
    return semanasList
      .filter(s => s.cierre?.cerrada)
      .reduce((a, s) => {
        const utilGuardada = s.cierre?.utilNeta || 0;
        const distTotal = Object.values(s.cierre?.dist || {}).reduce((b, v) => b + v, 0);
        return a + Math.max(0, utilGuardada - distTotal);
      }, 0);
  }, [semanasList]);


  function resetSemana(semanaKey) {
    setItem('semanas', semanaKey, {
      cerrada: true,
      dist: { Sol: 0, Bauti: 0, Andy: 0 },
      bounds: semanas[semanaKey]?.bounds || {},
      utilNeta: 0,
      margenUsado: margen,
      ajusteAplicado: 0,
      timestamp: Date.now(),
    });
    setResetConfirm(null);
    showToast('Semana reseteada a $0');
  }

  function openDistribuir(semana) {
    // Calc ajuste from previous week
    const prevIdx = semanasList.findIndex(s => s.key === semana.key);
    const prev = prevIdx < semanasList.length - 1 ? semanasList[prevIdx + 1] : null;
    let ajustePorSocio = {};
    let ajusteTotal = 0;

    if (prev?.cierre?.cerrada) {
      const realPrev = calcGananciaSemana(movimientos, costosMes, prev.desde, prev.hasta, margen, resFinPct);
      const distPrevTotal = Object.values(prev.cierre.dist || {}).reduce((a, b) => a + b, 0);
      const diff = realPrev.utilNeta - distPrevTotal;
      ajusteTotal = diff;
      SOCIOS.forEach(n => {
        const prop = distPrevTotal > 0 ? (prev.cierre.dist[n] || 0) / distPrevTotal : 1 / SOCIOS.length;
        ajustePorSocio[n] = diff * prop;
      });
    }

    const ganConAjuste = semana.datos.utilNeta + ajusteTotal;
    const tercio = ganConAjuste / 3;
    const form = {};
    SOCIOS.forEach(n => { form[n] = Math.round(tercio + (ajustePorSocio[n] || 0)); });

    setDistForm(form);
    setDistModal({ semana, ajusteTotal, ajustePorSocio, ganConAjuste });
  }

  function confirmarDistribucion() {
    const { semana, ajusteTotal, ajustePorSocio } = distModal;
    const dist = {};
    SOCIOS.forEach(n => { dist[n] = parseFloat(distForm[n]) || 0; });

    // Save semana cierre
    setItem('semanas', semana.key, {
      cerrada: true,
      dist,
      bounds: { desde: semana.desde, hasta: semana.hasta },
      utilNeta: semana.datos.utilNeta,
      margenUsado: margen,
      ajusteAplicado: ajusteTotal,
      timestamp: Date.now(),
    });

    // Acreditar en cuentas de socios
    SOCIOS.forEach(n => {
      const monto = dist[n];
      const socioData = Object.values(store.socios || {}).find(s => s.nombre === n) || { nombre: n, saldo: 0, movimientos: {} };
      const socioId = Object.keys(store.socios || {}).find(k => store.socios[k].nombre === n) || ('s_' + n);
      const mid = 'sg' + Date.now() + n;
      setItem('socios', socioId, {
        ...socioData,
        saldo: (parseFloat(socioData.saldo) || 0) + monto,
        movimientos: {
          ...socioData.movimientos,
          [mid]: {
            tipo: 'acreditacion', semana: semana.key, monto,
            ajuste: ajustePorSocio[n] || 0, fecha: hoy,
          }
        }
      });
    });

    showToast('Semana cerrada y ganancias acreditadas');
    setDistModal(null);
  }

  const totalDistribuidoEstaApertura = distModal
    ? Object.values(distForm).reduce((a, v) => a + (parseFloat(v) || 0), 0)
    : 0;

  return (
    <div>
      {/* Margen + acumulado */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', padding: '14px 16px', flex: 1 }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 8 }}>Margen bruto / CMV</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <input type="number" value={(margen * 100).toFixed(1)} step="0.1" min="1" max="99"
                onChange={e => { const v = parseFloat(e.target.value) / 100; if (!isNaN(v) && v > 0 && v < 1) { setMargen(v); localStorage.setItem('margen-bruto', v); } }}
                style={{ width: 65, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border2)', fontFamily: 'Courier, monospace', fontWeight: 700, fontSize: 16, background: 'var(--grn-l)', color: 'var(--grn)', textAlign: 'center' }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--grn)' }}>% margen</span>
            </div>
            <span style={{ fontSize: 16, color: 'var(--tx3)', fontWeight: 300 }}>+</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <input type="number" value={((1 - margen) * 100).toFixed(1)} step="0.1" min="1" max="99"
                onChange={e => { const v = parseFloat(e.target.value) / 100; if (!isNaN(v) && v > 0 && v < 1) { const m = 1 - v; setMargen(m); localStorage.setItem('margen-bruto', m); } }}
                style={{ width: 65, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border2)', fontFamily: 'Courier, monospace', fontWeight: 700, fontSize: 16, background: 'var(--red-l)', color: 'var(--red)', textAlign: 'center' }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--red)' }}>% CMV</span>
            </div>
            <span style={{ fontSize: 13, color: 'var(--tx3)' }}> = 100%</span>
            <div style={{ display: 'flex', gap: 5, marginLeft: 4 }}>
              {[30, 32, 34, 36].map(v => (
                <button key={v} onClick={() => { setMargen(v/100); localStorage.setItem('margen-bruto', v/100); }}
                  style={{ padding: '3px 9px', borderRadius: 99, border: `1px solid ${Math.abs(margen*100 - v) < 0.1 ? 'var(--grn)' : 'var(--border2)'}`, background: Math.abs(margen*100 - v) < 0.1 ? 'var(--grn-l)' : 'var(--surf2)', color: Math.abs(margen*100 - v) < 0.1 ? 'var(--grn)' : 'var(--tx2)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                  {v}%
                </button>
              ))}
            </div>
          </div>
        </div>
        <div style={{ background: 'var(--surf)', border: '1px solid var(--grn)', borderRadius: 'var(--rl)', padding: '14px 16px', flex: 1 }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 5 }}>Ganancia acumulada sin distribuir</div>
          <div style={{ fontSize: 26, fontWeight: 700, fontFamily: 'Courier, monospace', color: 'var(--grn)' }}>{fmtM(gananciasAcumuladas)}</div>
        </div>
      </div>

      {/* Resultados financieros */}
      <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', padding: '14px 16px', marginBottom: 16 }}>
        <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 8 }}>Resultados financieros (% de ventas que resta a la ganancia)</div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="number" value={(resFinPct * 100).toFixed(1)} step="0.5" min="0" max="20"
              onChange={e => { const v = parseFloat(e.target.value) / 100; if (!isNaN(v)) { setResFinPct(v); localStorage.setItem('res-fin-pct', v); } }}
              style={{ width: 65, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border2)', fontFamily: 'Courier, monospace', fontWeight: 700, fontSize: 18, background: 'var(--surf)', color: 'var(--tx)' }} />
            <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--tx3)' }}>%</span>
          </div>
          <div style={{ display: 'flex', gap: 5 }}>
            {[0, 2, 3, 4, 5].map(v => (
              <button key={v} onClick={() => { setResFinPct(v/100); localStorage.setItem('res-fin-pct', v/100); }}
                style={{ padding: '3px 9px', borderRadius: 99, border: `1px solid ${Math.abs(resFinPct*100 - v) < 0.1 ? 'var(--red)' : 'var(--border2)'}`, background: Math.abs(resFinPct*100 - v) < 0.1 ? 'var(--red-l)' : 'var(--surf2)', color: Math.abs(resFinPct*100 - v) < 0.1 ? 'var(--red)' : 'var(--tx2)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                {v === 0 ? 'Sin res. fin.' : `${v}%`}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: 'var(--tx3)', marginLeft: 8 }}>
            Sirve para amortizar deudas y generar flujo. Se resta antes de distribuir.
          </div>
        </div>
      </div>

      {/* Semanas */}
      {semanasList.map((semana, si) => {
        const { datos, cierre, ajuste, isCurrent } = semana;
        const cerrada = cierre?.cerrada;

        return (
          <div key={semana.key} style={{ background: 'var(--surf)', border: `1px solid ${isCurrent ? 'var(--yel)' : 'var(--border)'}`, borderRadius: 'var(--rl)', marginBottom: 12, overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ padding: '12px 16px', background: isCurrent ? '#FBF0D8' : 'var(--surf2)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>
                  {fmtDate(semana.desde)} — {fmtDate(semana.hasta)}
                  {isCurrent && <span style={{ marginLeft: 8, fontSize: 9, background: 'var(--yel-l)', color: 'var(--yel)', padding: '1px 6px', borderRadius: 99, fontWeight: 700 }}>SEMANA ACTUAL</span>}
                </div>
                <div style={{ fontSize: 11, color: 'var(--tx3)', marginTop: 2 }}>Ventas: {fmtM(datos.ventas)}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 9.5, color: 'var(--tx3)', fontWeight: 700, textTransform: 'uppercase' }}>Utilidad neta</div>
                  <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'Courier, monospace', color: datos.utilNeta >= 0 ? 'var(--grn)' : 'var(--red)' }}>{fmtM(datos.utilNeta)}</div>
                </div>
                {cerrada
                  ? <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: 'var(--grn-l)', color: 'var(--grn)' }}>Cerrada ✓</span>
                      {resetConfirm === semana.key
                        ? <div style={{ display: 'flex', gap: 4 }}>
                            <button onClick={() => setResetConfirm(null)} style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid var(--border2)', background: 'var(--surf2)', fontSize: 10, cursor: 'pointer' }}>Cancelar</button>
                            <button onClick={() => resetSemana(semana.key)} style={{ padding: '3px 8px', borderRadius: 6, border: 'none', background: 'var(--red)', color: '#fff', fontSize: 10, cursor: 'pointer', fontWeight: 700 }}>Confirmar $0</button>
                          </div>
                        : <button onClick={() => setResetConfirm(semana.key)} style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid var(--border2)', background: 'var(--surf2)', color: 'var(--tx3)', fontSize: 10, cursor: 'pointer' }}>↺ Reset</button>
                      }
                    </div>
                  : <Btn variant="grn" size="sm" onClick={() => openDistribuir(semana)}>Distribuir</Btn>}
              </div>
            </div>

            {/* Detail */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 0 }}>
              {[
                { l: 'Util. bruta', v: fmtM(datos.utilBruta), sub: `${(margen*100).toFixed(1)}% de ventas`, color: 'var(--grn)' },
                { l: 'Costos prorr.', v: `−${fmtM(datos.costoProrr)}`, sub: 'Por días del mes', color: 'var(--red)' },
                { l: 'Gastos var.', v: `−${fmtM(datos.gastosVar)}`, sub: 'Del período', color: 'var(--red)' },
                { l: `Res. financieros`, v: `-${fmtM(datos.resFin)}`, sub: `${(resFinPct*100).toFixed(1)}% de ventas`, color: 'var(--red)' },
              { l: ajuste !== 0 ? `Ajuste sem. ant.` : 'Sin ajuste', v: ajuste !== 0 ? `${ajuste >= 0 ? '+' : ''}${fmtM(ajuste)}` : '—', sub: ajuste !== 0 ? (ajuste >= 0 ? 'Se suma' : 'Se resta') : '', color: ajuste >= 0 ? 'var(--grn)' : 'var(--red)' },
              ].map((item, i) => (
                <div key={i} style={{ padding: '10px 16px', borderRight: i < 3 ? '1px solid var(--border)' : '', borderTop: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', marginBottom: 3 }}>{item.l}</div>
                  <div style={{ fontFamily: 'Courier, monospace', fontWeight: 700, fontSize: 13, color: item.color }}>{item.v}</div>
                  <div style={{ fontSize: 9.5, color: 'var(--tx3)' }}>{item.sub}</div>
                </div>
              ))}
            </div>

            {/* Distribución si está cerrada */}
            {cerrada && cierre.dist && (
              <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border)', display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', alignSelf: 'center' }}>Distribuido:</span>
                {SOCIOS.map(n => (
                  <div key={n} style={{ display: 'flex', align: 'center', gap: 6 }}>
                    <div style={{ width: 20, height: 20, borderRadius: '50%', background: COLORS_SOCIOS[n], display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 8, fontWeight: 700 }}>{ini(n)}</div>
                    <div style={{ fontSize: 12 }}><strong>{n}:</strong> <span style={{ fontFamily: 'Courier', fontWeight: 700, color: 'var(--grn)' }}>{fmtM(cierre.dist[n] || 0)}</span></div>
                    {cierre.ajusteAplicado && Math.abs(cierre.ajusteAplicado / 3) > 1 && (
                      <span style={{ fontSize: 9.5, color: cierre.ajusteAplicado >= 0 ? 'var(--grn)' : 'var(--red)' }}>
                        (aj: {cierre.ajusteAplicado >= 0 ? '+' : ''}{fmtM(cierre.ajusteAplicado / 3)})
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* MODAL DISTRIBUCIÓN */}
      {distModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, backdropFilter: 'blur(3px)' }}
          onClick={e => { if (e.target === e.currentTarget) setDistModal(null); }}>
          <div style={{ background: 'var(--surf)', borderRadius: 'var(--rl)', border: '1px solid var(--border2)', padding: 24, width: 480, boxShadow: '0 8px 40px rgba(0,0,0,.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700 }}>Distribuir ganancia semanal</h3>
              <button onClick={() => setDistModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--tx3)', fontSize: 22 }}>×</button>
            </div>

            {/* Summary */}
            <div style={{ background: 'var(--surf2)', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6 }}>
                <span style={{ color: 'var(--tx2)' }}>Utilidad neta semana</span>
                <span style={{ fontFamily: 'Courier', fontWeight: 700 }}>{fmtM(distModal.semana.datos.utilNeta)}</span>
              </div>
              {Math.abs(distModal.ajusteTotal) > 1 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6 }}>
                  <span style={{ color: distModal.ajusteTotal >= 0 ? 'var(--grn)' : 'var(--red)' }}>
                    Ajuste semana anterior {distModal.ajusteTotal >= 0 ? '(+)' : '(−)'}
                  </span>
                  <span style={{ fontFamily: 'Courier', fontWeight: 700, color: distModal.ajusteTotal >= 0 ? 'var(--grn)' : 'var(--red)' }}>
                    {distModal.ajusteTotal >= 0 ? '+' : ''}{fmtM(distModal.ajusteTotal)}
                  </span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 700, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                <span>A distribuir</span>
                <span style={{ fontFamily: 'Courier', color: distModal.ganConAjuste >= 0 ? 'var(--grn)' : 'var(--red)' }}>{fmtM(distModal.ganConAjuste)}</span>
              </div>
            </div>

            {/* Por socio */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 10 }}>Distribución por socio</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                {SOCIOS.map(n => (
                  <div key={n}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                      <div style={{ width: 22, height: 22, borderRadius: '50%', background: COLORS_SOCIOS[n], display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 9, fontWeight: 700 }}>{ini(n)}</div>
                      <span style={{ fontSize: 12, fontWeight: 600 }}>{n}</span>
                    </div>
                    {Math.abs(distModal.ajustePorSocio[n] || 0) > 1 && (
                      <div style={{ fontSize: 9.5, color: (distModal.ajustePorSocio[n] || 0) >= 0 ? 'var(--grn)' : 'var(--red)', marginBottom: 3 }}>
                        Ajuste: {(distModal.ajustePorSocio[n] || 0) >= 0 ? '+' : ''}{fmtM(distModal.ajustePorSocio[n] || 0)}
                      </div>
                    )}
                    <input type="number" value={distForm[n] || ''} onChange={e => setDistForm(f => ({ ...f, [n]: e.target.value }))}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border2)', fontFamily: 'Courier, monospace', fontWeight: 700, fontSize: 15, background: 'var(--surf)', color: 'var(--tx)', outline: 'none' }} />
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 10, fontSize: 11, color: 'var(--tx3)', textAlign: 'right' }}>
                Total a distribuir: <strong style={{ fontFamily: 'Courier', color: Math.abs(totalDistribuidoEstaApertura - distModal.ganConAjuste) < 100 ? 'var(--grn)' : 'var(--red)' }}>{fmtM(totalDistribuidoEstaApertura)}</strong>
                {' '}de {fmtM(distModal.ganConAjuste)}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
              <button onClick={() => setDistModal(null)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border2)', background: 'var(--surf2)', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--tx)' }}>Cancelar</button>
              <button onClick={confirmarDistribucion} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: 'var(--grn)', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Confirmar y acreditar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
